import { ContextService } from '../../services/context_service';
import { DataService } from '../../services/data_service';
import { DatenauswertungJahrauswahl } from './DatenauswertungJahrauswahl';
import { getCurrentConfig } from './helper/getReportConfigs';
import { getPDFViewerComponent } from './pdf/getPDFViewerComponent';
import { i18n } from '../../i18n/i18n';
import { IDs } from '../../helpers/IDs';
import { lazyInject } from '../../services/inversify';
import * as React from 'react';
import ReactHtmlParser from 'react-html-parser';
import style from './DatenauswertungReportKonfiguration.less';
import { TriggerService } from '../../core/trigger/trigger_service';
import { WActionButton } from '../../core/elements/WActionButton';
import { WFormFieldRichText, WFormFieldText } from '../../core/forms/WFormFramework';
import { WSpinner } from '../../core/elements/spinner/WSpinner';

interface DatenauswertungReportKonfigurationProps extends React.ClassAttributes<any> {
    clearPdfPreview: () => void;
    data: any;
    jahrFirst: number;
    jahrLast: number;
    jahrMax: number;
    jahrMin: number;
    onAction: (data: any) => void;
    onFirstJahrChange: (jahr: number) => void;
    onLastJahrChange: (jahr: number) => void;
    pages: any[];
    pdfPreviewData?: any;
    renderIndex: number;
}

interface DatenauswertungReportKonfigurationState {
    cache: any[];
    config: any;
    configs: any[];
    curConfig: number;
    modus: string;
    useContext: boolean;
}

export class DatenauswertungReportKonfiguration extends
    React.Component<DatenauswertungReportKonfigurationProps, DatenauswertungReportKonfigurationState> {

    constructor(props: any) {
        super(props);
        const stateContext = this.contextService.get('report_config');

        if (stateContext) {
            this.state = stateContext;
        } else {
            this.state = {
                cache: [],
                config: null,
                configs: null,
                curConfig: undefined,
                modus: 'bemerkungen',
                useContext: true
            };
        }
        this.getData = this.getData.bind(this);
        this.setPages = this.setPages.bind(this);
    }

    @lazyInject(ContextService) private contextService: ContextService;
    @lazyInject(DataService) private dataService: DataService;
    @lazyInject(TriggerService) private triggerService: TriggerService;

    private curBemerkung: number;
    private curNode: number;
    private curPage: number = null;
    private pages: any = {};
    private tree: any[];

    componentDidMount(): void {
        this.setState(
            { configs: this.getConfigs() },
            () => {
                this.setLatestConfigIndex();
                this.init();
            }
        );
    }

    componentDidUpdate(prevProps: any) {
        if (JSON.stringify(this.props.pages) !== JSON.stringify(prevProps.pages)) {
            this.init();
        }
    }

    componentWillUnmount(): void {
        console.log('Config', this.state);
        this.contextService.set('report_config', { ...this.state, useContext: false });
    }

    delData(): void {
        const index: number = typeof this.state.curConfig !== 'undefined' ? this.state.curConfig : -1;
        this.dataService.delData(
            IDs.ENT_REPORT, IDs.MOD_ETOOL, { tid: ((this.state.configs || [])[index] || {}).tid || null }, {},
            (res: any, success: boolean) => {
                if (success) {
                    const configs: any[] = this.state.configs || [];

                    if (index !== -1) {
                        configs.splice(index, 1);
                        this.setState({ configs, curConfig: undefined });
                    }
                    this.triggerService.fire('reload', IDs.ENT_REPORT);
                }
            }
        );
    }

    getBemerkungen(): any {
        const data: any = {};

        ((this.state.config || {}).selection || []).forEach((node: any) => {
            if ((typeof node.page === 'number' && node.active && node.checked && node.bemerkung)) {
                data[node.page] = node.bemerkung;
            }
        });

        return data;
    }

    getConfig(config: any): any {
        if (typeof config === 'string') {
            try {
                config = JSON.parse(config);
            } catch (error) {
                console.log(error.message);
            }
        }

        return config || {};
    }

    getConfigs(): any[] {
        return ((this.props.data || {})[IDs.ENT_REPORT] || []).map((report: any) =>
            ({ ...(this.getConfig(report.konfiguration || null) || {}), tid: report.tid || null, name: report.name || null })
        );
    }

    getData(): void {
        const index: number = typeof this.state.curConfig !== 'undefined' ? this.state.curConfig : -1;
        const config: any = (this.state.configs || [])[index] || {};
        const selection: any[] = [];
        const text: string = config.text || null;
        let isActiveGroup: boolean = false;
        let isActiveNode: boolean = false;

        ((this.state.config || {}).selection || []).forEach((node: any) => {
            const item: any = {};
            const data: any = (config.selection || [])
                .find((node2: any) => {
                    const group: boolean = typeof node.standort === 'number'
                        ? node2.standort === node.standort
                        : node2.category === node.category;
                    return group && node2.key === node.key && node2.sub === node.sub;
                }) || null;
            isActiveNode = typeof (data || node).key !== 'undefined' && typeof (data || node).sub === 'undefined'
                ? (data || node).checked : isActiveNode;
            isActiveGroup = typeof (data || node).key === 'undefined' ? (data || node).checked : isActiveGroup;

            Object.keys(data || node).forEach((key: string) => {
                item[key] = (data || node)[key];

                if (!data && ['active', 'checked'].indexOf(key) !== -1) {
                    item[key] = false;
                } else if (!data && key === 'bemerkung') {
                    item[key] = null;
                }
            });
            selection.push(item);
        });
        this.setState({ cache: config.selection || [], config: { selection, text } }, () => this.init(this.setPages));
    }

    getNodeStatus(data: any): any {
        const active: boolean = typeof data.active === 'boolean' ? data.active : false;
        const bemerkung: string = data.bemerkung || null;
        const category: number = typeof data.category === 'number' ? data.category : null;
        const checked: boolean = typeof data.checked === 'boolean' ? data.checked : true;
        const key: string = data.key || null;
        const dataBemerkung: any = (key && typeof data.page === 'number') ? { bemerkung } : {};
        const selection: any[] = (this.state.config || {}).selection || [];
        const standort: number = typeof data.standort === 'number' ? data.standort : null;
        const dataGroup: any = standort !== null ? { standort } : { category };
        const sub: string = data.sub || null;

        return selection.find((node: any) => {
            const group: boolean = standort !== null ? node.standort === standort : node.category === category;
            return group && (node.key || null) === key && (node.sub || null) === sub;
        })
        || (this.state.cache || []).find((node: any) => {
            const group: boolean = standort !== null ? node.standort === standort : node.category === category;
            return group && (node.key || null) === key && (node.sub || null) === sub;
        })
        || { ...dataGroup, ...(key && { key, active }), ...(sub && { sub }), checked, ...dataBemerkung };
    }

    getPagesBlacklist(): number[] {
        const data: number[] = [];

        ((this.state.config || {}).selection || []).forEach((node: any) => {
            if ((typeof node.page === 'number' && !node.checked)) {
                data.push(node.page);
            }
        });

        return data;
    }

    getProgress(): number {
        if (this.props.renderIndex < 0) {
            this.curPage = null;
        } else if (this.props.renderIndex >= 0 && typeof this.pages[this.props.renderIndex] === 'number') {
            this.curPage = this.pages[this.props.renderIndex];
        }

        return this.curPage !== null && Object.keys(this.pages).length
            ? Math.round(((this.curPage + 1) / Object.keys(this.pages).length) * 100)
            : null;
    }

    init(callback?: () => void): void {
        // console.log('ReportState at init:', this.state);
        const config: any = { selection: [], text: (this.state.config || {}).text || null };
        let category: number = -1;
        let indexGroup: number = -1;
        this.tree = [];

        (this.props.pages || []).forEach((item: any, index: number) => {
            const key: string = item.key || null;
            const page: number = item.page;
            const prevItem: any = this.props.pages[index - 1] || {};
            const prevKey: string = index ? prevItem.key || null : null;
            const prevStandort: number = index ? (prevItem.standort || {}).tid : null;
            const prevSub: any = index ? prevItem.sub || null : null;
            const standort: number = (item.standort || {}).tid;
            const isStandort: boolean = typeof standort === 'number';
            const sub: any = item.sub || null;

            if (isStandort && standort !== prevStandort) {
                this.tree.push({
                    name: (item.standort || {}).name || null,
                    standort,
                    children: []
                });
                config.selection.push(this.getNodeStatus({ standort }));
                ++indexGroup;
            } else if (!isStandort && category === -1) {
                ++category;
                this.tree.push({
                    name: 'Allgemein',
                    category,
                    children: []
                });
                config.selection.push(this.getNodeStatus({ category }));
                ++indexGroup;
            }
            const dataGroup: any = isStandort ? { standort } : { category };

            if (isStandort && sub !== null && (prevSub === null || (prevSub !== null && key !== prevKey))) {
                this.tree[indexGroup].children.push({
                    name: item.name || null,
                    standort,
                    key,
                    children: []
                });
                config.selection.push(this.getNodeStatus({ standort, key }));
            }

            if (isStandort && sub !== null) {
                const indexParent: number = this.tree[indexGroup].children.map((item2: any) => item2.key).indexOf(key);
                const status: any = this.getNodeStatus({ page, standort, key, sub: (sub || {}).key });
                this.tree[indexGroup].children[indexParent].children.push({
                    name: (sub || {}).name,
                    standort,
                    key,
                    sub: (sub || {}).key
                });
                config.selection.push({ ...status, page });
            } else {
                const status: any = this.getNodeStatus({ page, ...dataGroup, key });
                this.tree[indexGroup].children.push({
                    name: item.name || null,
                    ...dataGroup,
                    key
                });
                config.selection.push({ ...status, page });
            }
        });
        this.setState({ config }, () => typeof callback === 'function' ? callback() : null);
    }

    onChange(event: any): void {
        const config: any = this.state.config || {};
        const index: number = +event.target.value || 0;
        const checked: boolean = !!event.target.checked;
        const selection: any[] = config.selection || [];
        const node: any = selection[index] || {};
        const category: number = typeof node.category === 'number' ? node.category : null;
        const key: string = node.key || null;
        const standort: number = typeof node.standort === 'number' ? node.standort : null;
        const sub: string = node.sub || null;
        let groupIndex: number = null;
        let isEmptyGroup: boolean = true;
        let isEmptySub: boolean = null;
        let subIndex: number = null;

        if (sub) {
            node.checked = checked;
        }

        selection.forEach((item: any, index2: number) => {
            const group: boolean = standort !== null ? item.standort === standort : item.category === category;

            if (!sub && group && (!key || (key && item.key === key))) {
                item.checked = checked;
            }

            if (checked && group && typeof item.page !== 'number' && ((sub && item.key === key) || !item.key)) {
                item.checked = true;
            }

            if (group && (sub && item.key === key)) {
                if (item.sub) {
                    isEmptySub = item.checked ? false : (isEmptySub === null ? true : isEmptySub);
                } else {
                    subIndex = index2;
                }
            }

            if (group) {
                if (typeof item.page === 'number') {
                    isEmptyGroup = item.checked ? false : (isEmptyGroup === null ? true : isEmptyGroup);
                } else if (!item.key) {
                    groupIndex = index2;
                }
            }
        });

        if (isEmptyGroup && groupIndex !== null) {
            selection[groupIndex].checked = false;
        }

        if (isEmptySub && subIndex !== null) {
            selection[subIndex].checked = false;
        }
        this.setState({ config: { ...config, selection } }, this.setPages);
    }

    onChangeBemerkung(data: string, index: number): void {
        const config: any = this.state.config || {};
        const selection: any[] = config.selection || [];
        const node: any = selection[index] || {};
        node.bemerkung = data;
        this.setState({ config: { ...config, selection } });
    }

    onChangeReadonly(event: any): void {
        const config: any = this.state.config || {};
        const index: number = +event.target.value || 0;
        const checked: boolean = !!event.target.checked;
        const selection: any[] = config.selection || [];
        const node: any = selection[index] || {};
        node.active = checked;
        this.setState({ config: { ...config, selection } });
    }

    onChangeText(data: string): void {
        const config: any = this.state.config || {};
        config.text = data;
        this.setState({ config });
    }

    render(): JSX.Element {
        if (!this.state.config || !(this.tree || []).length) {
            return null;
        }
        // console.log('PDF Preview Data:', this.props.pdfPreviewData);
        const progress: number = this.getProgress();
        this.curBemerkung = -1;
        this.curNode = -1;
        // console.log('config: ', this.state.config);
        // console.log('configs: ', this.state.configs);
        // console.log('tree: ', this.tree);
        // console.log('curConfig: ', this.state.curConfig);
        return (
            <div className={style['report-konfiguration']}>
                <div>
                    <div style={{ marginLeft: -16 }}>
                        <DatenauswertungJahrauswahl
                            jahrMin={this.props.jahrMin}
                            jahrMax={this.props.jahrMax}
                            jahrFirst={this.props.jahrFirst}
                            jahrLast={this.props.jahrLast}
                            externalHeaderElements={[this.renderPdfButton()]}
                            onFirstJahrChange={(jahr: number) => this.props.onFirstJahrChange(jahr)}
                            onLastJahrChange={(jahr: number) => this.props.onLastJahrChange(jahr)}
                        />
                    </div>
                    {this.renderNodes(this.tree, 0)}
                </div>
                <div>
                    {this.renderActionbar()}
                    <div className={style.modus}>
                        {this.renderSelectModus()}
                    </div>
                    {this.state.modus === 'bemerkungen' &&
                        this.renderBemerkungen(this.tree, 0)
                    }
                    {this.state.modus === 'text' &&
                        <div className={style.text}>
                            {this.renderText()}
                        </div>
                    }
                </div>
                {this.props.pdfPreviewData &&
                    <div style={{ flex: 1, textAlign: 'center', backgroundColor: '#eee' }}>
                        {/* {this.renderPreviewButton()} */}
                        <button
                            style={{ height: 'var(--formfields-basis-height)', margin: 2 }}
                            onClick={() => this.props.clearPdfPreview()}
                        >
                            PDF-Vorschau schließen
                        </button>
                        {getPDFViewerComponent(this.props.pdfPreviewData)}
                    </div>
                }
                {progress !== null && <WSpinner label={progress === 100 ? 'PDF...' : `${progress}%`} />}
            </div>
        );
    }

    renderActionbar(): JSX.Element {
        return (
            <div>
                {this.renderSaveButton()}
                {this.renderSelectConfig()}
                {this.renderLoadButton()}
                {this.renderDeleteButton()}
                {this.renderResetButton()}
            </div>
        );
    }

    renderBemerkung(item: any, level: number, index: number, prevIndex: number = null): JSX.Element {
        const active: boolean = this.state.config.selection[this.curBemerkung].active || false;
        const disabled: boolean = !this.state.config.selection[this.curBemerkung].checked || false;
        const isHeaderStandort: boolean = typeof this.state.config.selection[this.curBemerkung].page !== 'number'
            && !this.state.config.selection[this.curBemerkung].key;
        const isHeaderSub: boolean = typeof this.state.config.selection[this.curBemerkung].page !== 'number'
            && this.state.config.selection[this.curBemerkung].key;

        if (item.category === 0) { // Allgemein ignorieren
            return null;
        }

        return (
            <div className={style[`level-${level}`]}>
                {(isHeaderStandort || isHeaderSub) &&
                    <>{`${level ? `${index + 1}. ` : ''}${item.name}`}</>
                }
                {(!isHeaderStandort && !isHeaderSub) && !(disabled || !active) &&
                    <WFormFieldText
                        label={
                            <div>
                                <input
                                    id={`active-${this.curBemerkung}`}
                                    type='checkbox'
                                    value={this.curBemerkung}
                                    checked={active}
                                    disabled={disabled}
                                    onChange={(event: any) => this.onChangeReadonly(event)}
                                />
                                <label htmlFor={`active-${this.curBemerkung}`}>
                                    {`${level > 1 ? `${prevIndex + 1}.` : ''}${index + 1}. ${item.name}`}
                                </label>
                            </div>
                        }
                        name={`bemerkung-${this.curBemerkung}`}
                        value={this.state.config.selection[this.curBemerkung].bemerkung}
                        isTextarea
                        disabled={disabled || !active}
                        onValueChange={(name: string, value: string) => this.onChangeBemerkung(value, +name.split('-')[1] || -1)}
                    />
                }
                {/*(!isHeaderStandort && !isHeaderSub) && !(disabled || !active) &&
                    <div className={style.richText}>
                        <WFormFieldRichText
                            label={
                                <div>
                                    <input
                                        id={`active-${this.curBemerkung}`}
                                        type='checkbox'
                                        value={this.curBemerkung}
                                        checked={active}
                                        disabled={disabled}
                                        onChange={(event: any) => this.onChangeReadonly(event)}
                                    />
                                    <label htmlFor={`active-${this.curBemerkung}`}>
                                        {`${level > 1 ? `${prevIndex + 1}.` : ''}${index + 1}. ${item.name}`}
                                    </label>
                                </div>
                            }
                            name={`bemerkung-${this.curBemerkung}`}
                            value={this.state.config.selection[this.curBemerkung].bemerkung}
                            height='200px'
                            onValueChange={(name: string, value: string) => this.onChangeBemerkung(value, +name.split('-')[1] || -1)}
                        />
                    </div>
                */}
                {(!isHeaderStandort && !isHeaderSub) && (disabled || !active) &&
                    <div className={style.richTextReadonly}>
                        <div>
                            <input
                                id={`active-${this.curBemerkung}`}
                                type='checkbox'
                                value={this.curBemerkung}
                                checked={active}
                                disabled={disabled}
                                onChange={(event: any) => this.onChangeReadonly(event)}
                            />
                            <label htmlFor={`active-${this.curBemerkung}`}>
                                {`${level > 1 ? `${prevIndex + 1}.` : ''}${index + 1}. ${item.name}`}
                            </label>
                        </div>
                        {this.state.config.selection[this.curBemerkung].bemerkung &&
                            <div style={{ whiteSpace: 'pre-line' }}>
                                {this.state.config.selection[this.curBemerkung].bemerkung}
                            </div>
                        }
                        {/*this.state.config.selection[this.curBemerkung].bemerkung &&
                            <div>{ReactHtmlParser(this.state.config.selection[this.curBemerkung].bemerkung)}</div>
                        */}
                    </div>
                }
            </div>
        );
    }

    renderBemerkungen(data: any[], level: number, prevIndex: number = null): JSX.Element[] {
        return data.map((item: any, index: number) => {
            ++this.curBemerkung;

            if (level === 0) {
                return (
                    <div key={`bemerkung-${this.curBemerkung}`}>
                        {this.renderBemerkung(item, level, index)}
                        {item.children && item.children.length > 0 && this.renderBemerkungen(item.children, level + 1)}
                    </div>
                );
            }

            return (
                <React.Fragment key={`bemerkung-${this.curBemerkung}`}>
                    {this.renderBemerkung(item, level, index, prevIndex)}
                    {item.children && item.children.length > 0 && this.renderBemerkungen(item.children, level + 1, index)}
                </React.Fragment>
            );
        });
    }

    renderDeleteButton(): JSX.Element {
        const progress: number = this.getProgress();
        const disabled: boolean = progress !== null || (typeof this.state.curConfig === 'undefined' || this.state.curConfig < 0);
        const styleProps: React.CSSProperties = disabled && { backgroundColor: 'lightgray', cursor: 'not-allowed' };

        return (
            <WActionButton
                label='Löschen'
                insideText='Löschen'
                key='delete'
                action='delete'
                disabled={disabled}
                styleProps={styleProps}
                onAction={() => {
                    const name: string = (((this.state.configs || [])[this.state.curConfig]) || {}).name;

                    if (name && confirm(`Konfiguration wirklich [${name}] entfernen?`)) {
                        this.delData();
                    }
                }}
            />
        );
    }

    renderLoadButton(): JSX.Element {
        const progress: number = this.getProgress();
        const disabled: boolean = progress !== null || (typeof this.state.curConfig === 'undefined' || this.state.curConfig < 0);
        const styleProps: React.CSSProperties = disabled && { backgroundColor: 'lightgray', cursor: 'not-allowed' };

        return (
            <WActionButton
                label='Laden'
                insideText='Laden'
                key='load'
                action='reload'
                disabled={disabled}
                styleProps={styleProps}
                onAction={() => this.setState({ config: null }, () => this.init(this.getData))}
            />
        );
    }

    renderNode(item: any, level: number, index: number): JSX.Element {
        return (
            <div
                className={style[`level-${level}`]}
                style={{ paddingLeft: level * 20 + 10 }}
            >
                {(level
                    ? <span style={{ minWidth: '1.5em', display: 'inline-flex', justifyContent: 'flex-end' }}>{`${index + 1}.`}</span>
                    : null
                )}
                <input
                    id={`input-${this.curNode}`}
                    type='checkbox'
                    value={this.curNode}
                    checked={this.state.config.selection[this.curNode].checked}
                    onChange={(event: any) => this.onChange(event)}
                />
                <label htmlFor={`input-${this.curNode}`}>
                    {item.name}
                </label>
            </div>
        );
    }

    renderNodes(data: any[], level: number): JSX.Element[] {
        return data.map((item: any, index: number) => {
            ++this.curNode;

            if (level === 0) {
                return (
                    <div key={`node-${this.curNode}`}>
                        {this.renderNode(item, level, index)}
                        {item.children && item.children.length > 0 && this.renderNodes(item.children, level + 1)}
                    </div>
                );
            }

            return (
                <React.Fragment key={`node-${this.curNode}`}>
                    {this.renderNode(item, level, index)}
                    {item.children && item.children.length > 0 && this.renderNodes(item.children, level + 1)}
                </React.Fragment>
            );
        });
    }

    renderPdfButton(): JSX.Element {
        const blacklist: number[] = this.getPagesBlacklist();
        const progress: number = this.getProgress();
        const disabled: boolean = progress !== null
            || !((this.state.config || {}).selection || []).reduce((checked: number, node: any) =>
                checked + (typeof node.page === 'number' && node.checked ? 1 : 0), 0
            );
        const styleProps: React.CSSProperties = disabled && { backgroundColor: 'lightgray', cursor: 'not-allowed' };

        return (
            <WActionButton
                label={i18n('PDF generieren', 'sys')}
                insideText={i18n('PDF generieren', 'sys')}
                key='pdf'
                action='pdf'
                disabled={disabled}
                styleProps={styleProps}
                onAction={() => {
                    this.contextService.set('report_config', this.state);
                    this.props.onAction({ blacklist });
                }}
            />
        );
    }

    renderPreviewButton(): JSX.Element {
        const progress: number = this.getProgress();
        const disabled: boolean = progress !== null;
        const styleProps: React.CSSProperties = disabled && { backgroundColor: 'lightgray', cursor: 'not-allowed' };

        return (
            <WActionButton
                label={i18n('PDF-Vorschau schließen', 'sys')}
                insideText={i18n('PDF-Vorschau schließen', 'sys')}
                key='preview'
                action='cancel'
                disabled={disabled}
                styleProps={styleProps}
                onAction={() => this.props.clearPdfPreview()}
            />
        );
    }

    renderResetButton(): JSX.Element {
        const progress: number = this.getProgress();
        const disabled: boolean = progress !== null;
        const styleProps: React.CSSProperties = disabled && { backgroundColor: 'lightgray', cursor: 'not-allowed' };

        return (
            <WActionButton
                label={i18n('Zurücksetzen', 'sys')}
                insideText={i18n('Zurücksetzen', 'sys')}
                key='reset'
                action='resetFilter'
                disabled={disabled}
                styleProps={styleProps}
                onAction={() => this.reset()}
            />
        );
    }

    renderSaveButton(): JSX.Element {
        const progress: number = this.getProgress();
        const disabled: boolean = progress !== null;
        const styleProps: React.CSSProperties = disabled && { backgroundColor: 'lightgray', cursor: 'not-allowed' };

        return (
            <WActionButton
                label={i18n('Speichern', 'sys')}
                insideText={i18n('Speichern', 'sys')}
                key='save'
                action='save'
                disabled={disabled}
                styleProps={styleProps}
                onAction={() => {
                    if (this.state.curConfig > -1) {
                        const name: string = (((this.state.configs || [])[this.state.curConfig]) || {}).name;

                        if (confirm(`Konfiguration [${name}] wirklich überschreiben?`)) {
                            this.setData(name);
                        }
                    } else {
                        this.setData(prompt('Name', new Date().toLocaleString()));
                    }
                }}
            />
        );
    }

    renderSelectConfig(): JSX.Element {
        const configs: any[] = this.state.configs || [];
        const options: JSX.Element[] = [];
        const progress: number = this.getProgress();
        const disabled: boolean = progress !== null;

        configs.forEach((config: any, index: number) => {
            options.push(
                <option key={`config-${index}`} value={index}>
                    {(config || {}).name || null}
                </option>
            );
        });

        return (
            <div>
                <label style={{ marginLeft: 5, cursor: 'default' }} htmlFor='select-config'>
                    Konfiguration:
                </label>
                <select
                    id='select-config'
                    style={{ margin: '0 5px', cursor: 'pointer', ...(disabled && { backgroundColor: 'lightgray', cursor: 'not-allowed' }) }}
                    value={this.state.curConfig}
                    onChange={(event: any) => this.setState({ curConfig: +event.target.value })}
                >
                    <option key='config-' value={-1}>
                        {`<${i18n('neu', 'sys')}>`}
                    </option>
                    {options}
                </select>
            </div>
        );
    }

    renderSelectModus(): JSX.Element[] {
        return [
            <label
                key='modus-bemerkungen'
                style={{ cursor: 'pointer', display: 'flex' }}
                htmlFor='modus-bemerkungen'
            >
                <input
                    id='modus-bemerkungen'
                    style={{ margin: 'auto 5px' }}
                    name='modus'
                    type='radio'
                    value='bemerkungen'
                    checked={this.state.modus === 'bemerkungen'}
                    onChange={(event: any) => this.setState({ modus: event.target.value || undefined })}
                />
                Bemerkungen
            </label>,
            <label
                key='modus-text'
                style={{ marginLeft: 5, cursor: 'pointer', display: 'flex' }}
                htmlFor='modus-text'
            >
                <input
                    id='modus-text'
                    style={{ margin: 'auto 5px' }}
                    name='modus'
                    type='radio'
                    value='text'
                    checked={this.state.modus === 'text'}
                    onChange={(event: any) => this.setState({ modus: event.target.value || undefined })}
                />
                Allgemeiner Text
            </label>
        ];
    }

    renderText(): JSX.Element {
        return (
            <WFormFieldText
                label={null}
                name='text-config'
                value={this.state.config.text}
                isTextarea
                onValueChange={(name: string, value: string) => this.onChangeText(value)}
            />
        );
    }

    reset(): void {
        this.setState({ cache: [], config: null, curConfig: -1 }, () => this.init(this.setPages));
    }

    setData(name: string): void {
        name = (name || '').trim();

        if (!name.length) {
            return;
        }
        const configs: any[] = this.state.configs || [];
        const index: number = typeof this.state.curConfig !== 'undefined' ? this.state.curConfig : -1;
        const selection: any[] = [];
        const text: string = typeof (this.state.config || {}).text === 'string' ? this.state.config.text : null;
        const tid: number = (configs[index] || {}).tid || null;

        ((this.state.config || {}).selection || []).forEach((node: any) => {
            const item: any = {};
            Object.keys(node).forEach((key: string) => key !== 'page' ? item[key] = node[key] : null);
            selection.push(item);
        });

        if (selection.length) {
            this.dataService.setData(
                IDs.MOD_ETOOL, IDs.ENT_REPORT,
                { tid, name, konfiguration: JSON.stringify({ selection, text }) },
                (insertID: number) => {
                    if (typeof insertID === 'number') {
                        configs.push({ tid: insertID, name, selection, text });
                    } else {
                        configs[index] = { tid, name, selection, text };
                    }
                    this.setState({ configs });
                    this.triggerService.fire('reload', IDs.ENT_REPORT);
                }
            );
        }
    }

    setLatestConfigIndex(): void {
        if (this.state.config || !this.state.configs) {
            return;
        }
        getCurrentConfig(this.state.configs || [], false, (data: any, index: number) => {
            if (index < 0) {
                return;
            }
            this.setState({ curConfig: index }, this.getData);
        });
    }

    setPages(): void {
        const blacklist: number[] = this.getPagesBlacklist();
        let index: number = -1;
        this.pages = {};

        (this.props.pages || []).forEach((item: any, page: number) => {
            if (blacklist.indexOf(page) === -1) {
                this.pages[page] = ++index;
            }
        });
    }
}
